import useSWR, { mutate } from 'swr';
import axios from 'utils/axios';
import fetcher from 'api/fetcher';
import { useKoleksiyonTumu, useSayfaliKoleksiyon } from 'hooks/useKoleksiyon';

// ==============================|| API - SAKİN SAYFALARI (PROFİL / ÖDEMELER / FİNANSAL DURUM / DUYURU / ANKET / BAŞVURULAR) ||============================== //
// Modül 3 Faz 3 ilk yarısı: Profil Bilgileri, Ödemeler, Apartman Finansal Durum.
// Modül 3 Faz 3 ikinci yarısı: Duyuru, Anket, Dilek/Öneri/Şikayet, Arıza Bildirimi, Karar Defteri.

const KEYS = {
  benimProfilim: 'kisiler/benim-profilim/',
  benimDairem: 'daireler/benim-dairem/',
  profilTalepleri: 'profil-guncelleme-talepleri/',
  aidatTahakkuklarim: 'aidat-tahakkuklar/',
  odemeGecmisim: 'gelir-giderler/',
  duyurular: 'duyurular/',
  duyuruEkleri: 'duyuru-ekleri/',
  kararDefterleri: 'karar-defterleri/',
  anketler: 'anketler/',
  anketSorulari: 'anket-sorulari/',
  anketSecenekleri: 'anket-secenekleri/',
  anketOylari: 'anket-oylari/',
  dilekOneriSikayetler: 'dilek-oneri-sikayetler/',
  arizaBildirimleri: 'ariza-bildirimleri/',
  arizaDosyalari: 'ariza-dosyalari/',
  bildirimler: 'bildirimler/',
  bildirimlerArsiv: 'bildirimler/arsiv/',
  bildirimlerOkunmamisSayisi: 'bildirimler/okunmamis-sayisi/'
};

// finans.js/yonetim.js'teki ortak iskelet — Yönetici CRUD ekranları bu
// yardımcıyla create/update/remove/mutate setini tek satırda üretir.
function kaynakOlustur(anahtar) {
  return {
    create: (payload) => axios.post(anahtar, payload).then((res) => res.data),
    update: (id, payload) => axios.patch(`${anahtar}${id}/`, payload).then((res) => res.data),
    remove: (id) => axios.delete(`${anahtar}${id}/`),
    mutate: () => mutate(anahtar)
  };
}

// KisiViewSet.benim_profilim, kişi kaydı olmayan (örn. Yönetici) kullanıcı için
// 404 döner — bu, "veri yok" anlamına gelir, SWR hata durumu değil, null veri
// olarak ele alınır (bkz. axios interceptor'ın hata gövdesini 'detail' alanına
// indirgemesi, utils/axios.js).
const KISI_KAYDI_YOK_MESAJI = 'Kişi kaydınız bulunamadı.';

const DAIRE_ILISKISI_YOK_MESAJI = 'Herhangi bir daireyle ilişkiniz bulunmuyor.';

function kayitYoksaNullFetcher(yokMesaji) {
  return async (url) => {
    try {
      return await fetcher(url);
    } catch (error) {
      if (error && error.detail === yokMesaji) {
        return null;
      }
      throw error;
    }
  };
}

const benimProfilimFetcher = kayitYoksaNullFetcher(KISI_KAYDI_YOK_MESAJI);
const benimDairemFetcher = kayitYoksaNullFetcher(DAIRE_ILISKISI_YOK_MESAJI);

export function useBenimProfilim() {
  const { data, isLoading } = useSWR(KEYS.benimProfilim, benimProfilimFetcher);
  return { profil: data ?? null, profilLoading: isLoading };
}

// DaireViewSet.benim_dairem: sakinin aktif dairesinin bakiyesi (avans) ve
// tahakkuk etmiş borç toplamı (aidat + ek ödenek + manuel alacak, kalan tutar).
export function useBenimDairem() {
  const { data, isLoading } = useSWR(KEYS.benimDairem, benimDairemFetcher);
  return { benimDairem: data ?? null, benimDairemLoading: isLoading };
}

// ProfilBilgileri.jsx (sakin, kendi talebi) ve ProfilTalepleriListesi.jsx (admin
// tablosu, küçük hacim) — ikisi de tam listeyi kullanır.
export function useProfilTalepleri() {
  return useKoleksiyonTumu(KEYS.profilTalepleri, 'profilTalepleri');
}
// ProfilTalepleriListesi.jsx admin tablosu büyürse sayfalı hook (Faz 3'te bağlanır).
export function useProfilTalepleriSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.profilTalepleri, 'profilTalepleri', params);
}

export function mutateProfilTalepleri() {
  return mutate((key) => typeof key === 'string' && key.startsWith(KEYS.profilTalepleri));
}

export function createProfilTalebi(payload) {
  return axios.post(KEYS.profilTalepleri, payload).then((res) => res.data);
}

export function useAidatTahakkuklarim() {
  return useKoleksiyonTumu(KEYS.aidatTahakkuklarim, 'aidatTahakkuklarim');
}

export function useOdemeGecmisim() {
  return useKoleksiyonTumu(KEYS.odemeGecmisim, 'odemeGecmisim');
}

export function parolaDegistir(payload) {
  return axios.post('auth/parola-degistir/', payload).then((res) => res.data);
}

// DuyuruListesi.jsx (sakin feed) — tam listeyi kullanır.
export function useDuyurular() {
  return useKoleksiyonTumu(KEYS.duyurular, 'duyurular');
}

// Yönetici Duyuru Yönetimi (Bölüm 4.3): durum=arsiv/blok/tarih aralığı filtreleri +
// sayfa/arama backend'in DuyuruViewSet'ine (DuyuruFilterSet + SearchFilter + pagination)
// query param olarak gider.
export function useDuyurularYonetici(params = {}) {
  const { duyurular, duyurularToplam, duyurularLoading } = useSayfaliKoleksiyon(KEYS.duyurular, 'duyurular', params);
  const mutateDuyurular = () => mutate((key) => typeof key === 'string' && key.startsWith(KEYS.duyurular));
  return { duyurular, duyurularToplam, duyurularLoading, mutateDuyurular };
}

export const duyuruKaynagi = kaynakOlustur(KEYS.duyurular);

export function duyuruEkiYukle(formData) {
  return axios.post(KEYS.duyuruEkleri, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((res) => res.data);
}

export function duyuruKimlerOkudu(id) {
  return axios.get(`${KEYS.duyurular}${id}/kimler-okudu/`).then((res) => res.data);
}

export function useKararDefterleriSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.kararDefterleri, 'kararDefterleri', params);
}

export function mutateKararDefterleri() {
  return mutate((key) => typeof key === 'string' && key.startsWith(KEYS.kararDefterleri));
}

export const kararDefteriKaynagi = kaynakOlustur(KEYS.kararDefterleri);

// AnketYonetimi.jsx (admin), AnketListesi.jsx (sakin feed), AnketSoruYonetimDialog.jsx
// (dropdown) — üçü de tam listeyi kullanıyor.
export function useAnketler() {
  return useKoleksiyonTumu(KEYS.anketler, 'anketler');
}
// AnketYonetimi.jsx admin tablosu büyürse sayfalı hook (Faz 3'te bağlanır).
export function useAnketlerSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.anketler, 'anketler', params);
}

export function mutateAnketler() {
  return mutate((key) => typeof key === 'string' && key.startsWith(KEYS.anketler));
}

export function oyKullan(payload) {
  return axios.post(KEYS.anketOylari, payload).then((res) => res.data);
}

// -- Anket Yönetimi (Yönetici — Bölüm 5.2/5.3) --

export const anketKaynagi = kaynakOlustur(KEYS.anketler);
export const anketSoruKaynagi = kaynakOlustur(KEYS.anketSorulari);
export const anketSecenegiKaynagi = kaynakOlustur(KEYS.anketSecenekleri);

export function anketSonucuYayimla(id) {
  return axios.post(`${KEYS.anketler}${id}/sonucu-yayimla/`).then((res) => res.data);
}

// Bölüm 5.4: anket sonuçlarının Excel olarak dışa aktarımı — JWT header'ı
// ancak axios isteğiyle taşınır (bkz. finans.js sayacSablonIndir aynı desen).
export async function anketSonuclariDisaAktar(id) {
  const response = await axios.get(`${KEYS.anketler}${id}/disa-aktar/`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = `anket_sonuclari_${id}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function useDilekOneriSikayetlerSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.dilekOneriSikayetler, 'dilekOneriSikayetler', params);
}

export function mutateDilekOneriSikayetler() {
  return mutate((key) => typeof key === 'string' && key.startsWith(KEYS.dilekOneriSikayetler));
}

export function createDilekOneriSikayet(payload) {
  return axios.post(KEYS.dilekOneriSikayetler, payload).then((res) => res.data);
}

export function useArizaBildirimleriSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.arizaBildirimleri, 'arizaBildirimleri', params);
}

export function mutateArizaBildirimleri() {
  return mutate((key) => typeof key === 'string' && key.startsWith(KEYS.arizaBildirimleri));
}

export function createArizaBildirimi(payload) {
  return axios.post(KEYS.arizaBildirimleri, payload).then((res) => res.data);
}

export function uploadArizaDosyasi(formData) {
  return axios.post(KEYS.arizaDosyalari, formData).then((res) => res.data);
}

// ==============================|| API - BİLDİRİMLER (Modül 4 Faz 3, Yönetici-only) ||============================== //

export function useBildirimlerSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.bildirimler, 'bildirimler', params);
}

export function useBildirimlerArsivSayfali(params) {
  return useSayfaliKoleksiyon(KEYS.bildirimlerArsiv, 'bildirimlerArsiv', params);
}

export function useOkunmamisSayisi() {
  const { data } = useSWR(KEYS.bildirimlerOkunmamisSayisi, fetcher);
  return { sayi: data?.sayi ?? 0 };
}

// Bildirim işlemleri (okundu/arşiv/sil) aynı anda aktif liste, arşiv listesi ve
// rozet sayısını etkileyebildiğinden üçü birlikte mutate edilir.
export function mutateBildirimler() {
  // KEYS.bildirimlerArsiv ve KEYS.bildirimlerOkunmamisSayisi de 'bildirimler/' ile
  // başladığından tek bir prefix-eşleşmesi üçünü de kapsar.
  return mutate((key) => typeof key === 'string' && key.startsWith(KEYS.bildirimler));
}

export function okunduIsaretle(id) {
  return axios.post(`${KEYS.bildirimler}${id}/okundu-isaretle/`).then((res) => res.data);
}

export function tumunuOkunduIsaretle() {
  return axios.post(`${KEYS.bildirimler}tumunu-okundu-isaretle/`).then((res) => res.data);
}

export function topluOkunduIsaretle(ids) {
  return axios.post(`${KEYS.bildirimler}toplu-okundu-isaretle/`, { ids }).then((res) => res.data);
}

export function arsivle(id) {
  return axios.post(`${KEYS.bildirimler}${id}/arsivle/`).then((res) => res.data);
}

export function arsivdenCikar(id) {
  return axios.post(`${KEYS.bildirimler}${id}/arsivden-cikar/`).then((res) => res.data);
}

export function bildirimSil(id) {
  return axios.delete(`${KEYS.bildirimler}${id}/`);
}

export function topluSil(ids) {
  return axios.post(`${KEYS.bildirimler}toplu-sil/`, { ids }).then((res) => res.data);
}

export function sikayetKaydiKapat(id, yoneticiAciklamasi) {
  return axios.post(`${KEYS.dilekOneriSikayetler}${id}/kaydi-kapat/`, { yonetici_aciklamasi: yoneticiAciklamasi }).then((res) => res.data);
}

export function arizaKaydiKapat(id, yoneticiAciklamasi) {
  return axios.post(`${KEYS.arizaBildirimleri}${id}/kaydi-kapat/`, { yonetici_aciklamasi: yoneticiAciklamasi }).then((res) => res.data);
}

export function profilTalebiOnayla(id) {
  return axios.post(`${KEYS.profilTalepleri}${id}/onayla/`).then((res) => res.data);
}

export function profilTalebiReddet(id, redGerekcesi) {
  return axios.post(`${KEYS.profilTalepleri}${id}/reddet/`, { red_gerekcesi: redGerekcesi }).then((res) => res.data);
}

export function useSikayetDetay(id) {
  const { data, isLoading } = useSWR(id ? `${KEYS.dilekOneriSikayetler}${id}/` : null, fetcher);
  return { sikayet: data ?? null, sikayetLoading: isLoading };
}

export function useArizaDetay(id) {
  const { data, isLoading } = useSWR(id ? `${KEYS.arizaBildirimleri}${id}/` : null, fetcher);
  return { ariza: data ?? null, arizaLoading: isLoading };
}

export function useProfilTalebiDetay(id) {
  const { data, isLoading } = useSWR(id ? `${KEYS.profilTalepleri}${id}/` : null, fetcher);
  return { profilTalebi: data ?? null, profilTalebiLoading: isLoading };
}
